import { CH_ACCOUNT } from 'constants/general';
import { initialize, reset } from 'redux-form';
import {
  SIGN_UP_CHAMPION_REQUEST,
  SIGN_UP_CHAMPION_SUCCESS,
  SIGN_UP_CHAMPION_FAILURE,
  LOG_IN_CHAMPION_REQUEST,
  LOG_IN_CHAMPION_SUCCESS,
  LOG_IN_CHAMPION_FAILURE,
  GET_CHAMPION_DATA_REQUEST,
  GET_CHAMPION_DATA_SUCCESS,
  GET_CHAMPION_DATA_FAILURE,
  SUBMIT_CHAMPION_SOCIAL_FORM_REQUEST,
  SUBMIT_CHAMPION_SOCIAL_FORM_SUCCESS,
  SUBMIT_CHAMPION_SOCIAL_FORM_FAILURE,
  CLEAR_CHAMPION_SOCIAL_FORM,
  GET_CHAMPION_PROFILE_DATA_REQUEST,
  GET_CHAMPION_PROFILE_DATA_SUCCESS,
  GET_CHAMPION_PROFILE_DATA_FAILURE,
  GET_CHAMPION_PROFILE_POOLS_REQUEST,
  GET_CHAMPION_PROFILE_POOLS_SUCCESS,
  GET_CHAMPION_PROFILE_POOLS_FAILURE,
  GET_CHAMPION_POOL_TILES_REQUEST,
  GET_CHAMPION_POOL_TILES_SUCCESS,
  GET_CHAMPION_POOL_TILES_FAILURE,
  SUBMIT_CHAMPION_STAKE_FORM_REQUEST,
  SUBMIT_CHAMPION_STAKE_FORM_SUCCESS,
  SUBMIT_CHAMPION_STAKE_FORM_FAILURE,
  CLEAR_CHAMPION_STAKE_FORM,
  LOG_OUT_CHAMPION,
  SET_CHAMPION_HAS_OWN_POOL,
  GET_POOL_LIST_REQUEST,
  GET_POOL_LIST_SUCCESS,
  GET_POOL_LIST_FAILURE,
} from '../actionTypes/championActionTypes';
import {
  championSignUp,
  stakeToChampionNft,
  getChampionProfileData,
  getChampionOwnerData,
  submitChampionOwnerSocialsForm,
  getChampionProfilePools,
  getChampionPoolsTiles,
} from '../../services/championService';
import { APPROVE_TYPES } from '../../constants/assets';
import { wait } from '../../services/utilsService';
import { approveAddressOnAssetAction, getAssetBalanceAction } from './assetsActions';
import { hordTicketManagerAddress } from '../../services/contractsRegistryService';
import { CHAMPION_VERIFIED_STATES } from '../../constants/champion';
import { createNotification, startAction, stopAction } from './uiActions';
import { getChampion, getHPools } from '../../services/createHPoolService';

/**
 * Handles redux state when champion tries to sign up
 * @return {Function}
 */
export const championSignUpAction = () => async (dispatch) => {
  dispatch({ type: SIGN_UP_CHAMPION_REQUEST });

  try {
    championSignUp();
    await wait(10000);

    dispatch({ type: SIGN_UP_CHAMPION_SUCCESS });
  } catch (err) {
    dispatch({ type: SIGN_UP_CHAMPION_FAILURE, payload: err.message });
  }
};

/**
 * Handles redux state when champion tries to log in
 *
 * @param history
 * @param closeModal
 * @returns {function(*): Promise<void>}
 */
export const championLoginAction = (history, closeModal) => async (dispatch) => {
  dispatch({ type: LOG_IN_CHAMPION_REQUEST });

  try {
    championSignUp();
    await wait(10000);

    dispatch({ type: LOG_IN_CHAMPION_SUCCESS });
    closeModal();
  } catch (err) {
    dispatch({ type: LOG_IN_CHAMPION_FAILURE, payload: err.message });
  }
};

/**
 * Handles redux state when champion tries to log out
 *
 * @param pathName {String}
 * @param history {Object}
 * @returns {function(): void}
 */
export const logoutChampionAction = (pathName, history) => (dispatch, getState) => {
  window.localStorage.removeItem(CH_ACCOUNT);

  const { championData } = getState().champion;
  const unverified =
    championData.is_verified_champion === CHAMPION_VERIFIED_STATES.UNVERIFIED ||
    championData.is_verified_champion === CHAMPION_VERIFIED_STATES.IN_PROGRESS;

  if (unverified || pathName !== '/') history.push('/');

  dispatch({ type: LOG_OUT_CHAMPION });
};

/**
 * Handles redux state when trying to get champion Data
 *
 * @returns {function(*): Promise<void>}
 */
export const getChampionOwnerDataAction = (token, history) => async (dispatch) => {
  dispatch({ type: GET_CHAMPION_DATA_REQUEST });

  try {
    const payload = (await getChampionOwnerData(token)).user_metadata;

    dispatch({ type: GET_CHAMPION_DATA_SUCCESS, payload });

    window.localStorage.setItem(CH_ACCOUNT, token);

    if (history) history.push(`/champion/${payload.handle}`);
  } catch (err) {
    const errMsg = err?.message || err;
    dispatch({ type: GET_CHAMPION_DATA_FAILURE, payload: errMsg });
    dispatch(createNotification('error', errMsg));
    history.push('/');
  }
};

export const getPoolListAction = () => async (dispatch) => {
  dispatch({ type: GET_POOL_LIST_REQUEST });

  try {
    const hpools = await getHPools();

    dispatch({ type: GET_POOL_LIST_SUCCESS, payload: hpools });
  } catch (err) {
    dispatch({ type: GET_POOL_LIST_FAILURE, payload: err.message });
  }
};

/**
 * Handles redux state when champion tries to add social
 *
 * @returns {function(*): Promise<void>}
 */
export const submitChampionOwnerSocialsFormAction = (data) => async (dispatch) => {
  dispatch({ type: SUBMIT_CHAMPION_SOCIAL_FORM_REQUEST });

  try {
    const payload = await submitChampionOwnerSocialsForm(data);

    dispatch({ type: SUBMIT_CHAMPION_SOCIAL_FORM_SUCCESS, payload: payload.socials });

    dispatch(
      initialize('championSocialForm', {
        ...payload.socials,
        otherName: payload.socials.misc_social_link_name_1,
        otherUrl: payload.socials.misc_social_link_url_1,
      }),
    );
  } catch (err) {
    dispatch({ type: SUBMIT_CHAMPION_SOCIAL_FORM_FAILURE, payload: err.message });
  }
};

/**
 * Handles redux state for clearing the champion buying ticket state
 *
 * @returns {function(*): *}
 */
export const clearChampionOwnerSocialsFormAction = () => (dispatch) => dispatch({ type: CLEAR_CHAMPION_SOCIAL_FORM });

/**
 * Handles redux state when trying to get champion data as viewer
 *
 * @returns {function(): void}
 */
export const getChampionProfileDataAction = (handle) => async (dispatch) => {
  dispatch({ type: GET_CHAMPION_PROFILE_DATA_REQUEST });

  try {
    const payload = (await getChampionProfileData(handle)).champion;

    dispatch({ type: GET_CHAMPION_PROFILE_DATA_SUCCESS, payload });
  } catch (err) {
    dispatch({ type: GET_CHAMPION_PROFILE_DATA_FAILURE, payload: err.message });
  }
};

/**
 * Handles redux state when trying to get champion pools
 *
 * @returns {function(): void}
 */
export const getChampionProfilePoolsAction = (handle) => async (dispatch) => {
  dispatch({ type: GET_CHAMPION_PROFILE_POOLS_REQUEST });

  try {
    const payload = await getChampionProfilePools(handle);

    dispatch({ type: GET_CHAMPION_PROFILE_POOLS_SUCCESS, payload });
  } catch (err) {
    dispatch({ type: GET_CHAMPION_PROFILE_POOLS_FAILURE, payload: err.message });
  }
};

/**
 * Handles redux state when trying to get champion pools
 *
 * @returns {function(): void}
 */
export const getChampionPoolTilesAction = (handle) => async (dispatch) => {
  dispatch({ type: GET_CHAMPION_POOL_TILES_REQUEST });

  try {
    const payload = await getChampionPoolsTiles(handle);
    dispatch({ type: GET_CHAMPION_POOL_TILES_SUCCESS, payload });
  } catch (err) {
    dispatch({ type: GET_CHAMPION_POOL_TILES_FAILURE, payload: err.message });
  }
};

/**
 * Handles redux state when champion tries to stake
 *
 * @returns {function(*): Promise<void>}
 */
export const submitChampionViewerStakeFormAction =
  ({ stakeAmount }, assetApproved, handle, nftId) =>
  async (dispatch, getState) => {
    const { account } = getState().wallet;

    dispatch({ type: SUBMIT_CHAMPION_STAKE_FORM_REQUEST, handle, account });

    try {
      if (!assetApproved) {
        await dispatch(approveAddressOnAssetAction('HORD', hordTicketManagerAddress, APPROVE_TYPES.STAKING));
      }
      await stakeToChampionNft(account, stakeAmount, nftId);

      dispatch({ type: SUBMIT_CHAMPION_STAKE_FORM_SUCCESS, handle, account });
      dispatch(getAssetBalanceAction('HORD'));
      dispatch(reset('championStakeForm'));
    } catch (err) {
      dispatch({
        type: SUBMIT_CHAMPION_STAKE_FORM_FAILURE,
        handle,
        account,
        payload: err.message,
      });
    }
  };

/**
 * Handles redux state for clearing the champion nft staking state
 *
 * @param handle {String}
 * @param account {String}
 * @returns {function(*): *}
 */
export const clearChampionViewerStakeFormStateAction = (handle, account) => ({
  type: CLEAR_CHAMPION_STAKE_FORM,
  handle,
  account,
});

export const setChampionHasOwnPool = (championHasOwnPool) => ({
  type: SET_CHAMPION_HAS_OWN_POOL,
  payload: championHasOwnPool,
});
